/* cSpell:disable */
import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

import { CoreHelper, FormatHelper } from '@bcp-channels-sharedcomponents-reactjs/core/dist/$helpers';
import { useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { TranslationHookOutput } from '@bcp-channels-sharedcomponents-reactjs/core/dist/types';
import { I18nFormatHelper } from '@helpers';
import { useGetCurrentBreakpoint, useLogger } from '@hooks';
import { useSessionInfo } from '@store';
import { BaseSessionInfoLanguage, ViewSizeType } from '@types';

import { SelectValueProps } from './selectValue';

export interface UseSelectValueHookOutput {
	isCustomValue: boolean;
	editingCustomValue: boolean;
	isValidCustomValue: boolean;
	lastEditedValue: number;
	handleOnChangeValue: (v: number) => void;
	handleSetCustomValue: () => void;
	handleCustomInputChange: (v: number) => void;
	handleCompleteEditing: () => void;
	handleOnEnterKey: (e: React.KeyboardEvent<HTMLElement>) => void;
	currentView: ViewSizeType;
	translate: TranslationHookOutput;
	handleMobileCompleteEditing: () => void;
	showCustomValueErrorMessage: boolean;
	updateShowCustomValueErrorMessage: Dispatch<SetStateAction<boolean>>;
	handleShowCustomValueErrorMessage: () => boolean;
	hideCustomValueErrorMessage: () => void;
	formatAmountNumber: (value: number) => string;
	getCurrencyGroupSeparetor: () => string;
	getCurrencyDecimalSeparator: () => string;
	hoveredElement?: number;
	setHoveredElement: React.Dispatch<React.SetStateAction<number | undefined>>;
	// isHover: boolean;
	isOtherValueHover: boolean;
	setIsOtherValueHover: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useSelectValue = (props: SelectValueProps): UseSelectValueHookOutput => {
	const logger = useLogger();
	logger('SelectValue > render', props);
	const translate = useTranslation();
	const { defaultValues, onChange, value, minValue, maxValue } = props;
	const [isCustomValue, updateIsCustomValue] = useState(false);
	const [editingCustomValue, updateEditingCustomValue] = useState(false);
	const [isValidCustomValue, updateIsValidCustomValue] = useState(false);
	const [isInitialRender, setIsInitialRender] = useState(true);
	const [isOtherValueHover, setIsOtherValueHover] = useState(false);
	const [showCustomValueErrorMessage, updateShowCustomValueErrorMessage] = useState(false);
	const lastEditedValue = useRef<number>(null);
	const { currentView } = useGetCurrentBreakpoint();
	const sessionInfo = useSessionInfo();
	const language = sessionInfo.language;
	// const [isHover, setIsHover] = React.useState<boolean[]>([]);
	const [hoveredElement, setHoveredElement] = useState<number | undefined>(undefined);
	const toggleIsCustomValue = React.useCallback(
		(isCustomValue: boolean) => {
			logger('SelectValue > toggleIsCustomValue', isCustomValue);
			updateIsCustomValue(isCustomValue);
		},
		[logger]
	);

	const handleOnChangeValue = React.useCallback(
		(v: number) => {
			const lastEdition = lastEditedValue.current || 0;
			const isValid = lastEdition >= minValue && lastEdition <= maxValue;
			const isCustomValue = !defaultValues?.includes(lastEdition);
			setIsOtherValueHover(false);
			updateIsValidCustomValue(isCustomValue && isValid);
			onChange(v, true);
			toggleIsCustomValue(false);
			updateEditingCustomValue(false);
		},
		[defaultValues, maxValue, minValue, onChange, toggleIsCustomValue]
	);

	const handleSetCustomValue = React.useCallback(() => {
		toggleIsCustomValue(true);
		updateEditingCustomValue(true);
		onChange(value || 0, false);
	}, [lastEditedValue, onChange, toggleIsCustomValue]);

	const handleCustomInputChange = React.useCallback(
		(value: any) => {
			const isValid = value >= minValue && value <= maxValue;
			onChange(value, false);
			lastEditedValue.current = value;
			updateIsValidCustomValue(isValid);
		},
		[maxValue, minValue, onChange]
	);

	const handleCompleteEditing = React.useCallback(() => {
		const isCustomValue = !defaultValues?.includes(value);
		updateIsCustomValue(isCustomValue);
		updateIsValidCustomValue(isCustomValue);
		updateEditingCustomValue(false);
		onChange(value);
	}, [defaultValues, value]);

	const handleOnEnterKey = (e: React.KeyboardEvent<HTMLElement>) => {
		if (e.key === 'Enter' && isValidCustomValue) {
			handleCompleteEditing();
		}
	};

	const handleMobileCompleteEditing = () => {
		if (value < minValue || value > maxValue) {
			updateShowCustomValueErrorMessage(true);
		}

		if (isValidCustomValue && !(value < minValue || value > maxValue) && CoreHelper.isNullOrEmpty(hoveredElement)) {
			handleCompleteEditing();
		}
	};

	const handleShowCustomValueErrorMessage = () =>
		showCustomValueErrorMessage && (value < minValue || value > maxValue);

	const hideCustomValueErrorMessage = () => {
		if ((value >= minValue || value <= maxValue) && value !== 0) {
			updateIsValidCustomValue(true);
		}
		updateShowCustomValueErrorMessage(false);
	};

	// const hanldeOnOver = React.useCallback((v: number) => {
	// 	const newIsHover = [...isHover,]
	// 	setIsHover();
	// }, []);

	const formatAmountNumber = (value: number) => {
		return language.toLowerCase() === BaseSessionInfoLanguage.EN.toLowerCase()
			? I18nFormatHelper.formatAmountToEnglish(value)
			: FormatHelper.formatNumber(value, 'integer', 0);
	};

	const getCurrencyGroupSeparetor = () =>
		language.toLowerCase() === BaseSessionInfoLanguage.EN.toLowerCase() ? ',' : '.';

	const getCurrencyDecimalSeparator = () =>
		language.toLowerCase() === BaseSessionInfoLanguage.EN.toLowerCase() ? '.' : ',';

	useEffect(() => {
		if (isInitialRender && !defaultValues?.includes(+value) && value >= minValue && value <= maxValue) {
			toggleIsCustomValue(true);
			setIsInitialRender(false);
			lastEditedValue.current = value as any;
			updateIsValidCustomValue(true);
		}
	}, [value]);

	return {
		isCustomValue,
		editingCustomValue,
		isValidCustomValue,
		handleOnChangeValue,
		handleSetCustomValue,
		handleCustomInputChange,
		handleCompleteEditing,
		lastEditedValue: lastEditedValue.current || 0,
		handleOnEnterKey,
		currentView,
		translate,
		handleMobileCompleteEditing,
		showCustomValueErrorMessage,
		updateShowCustomValueErrorMessage,
		handleShowCustomValueErrorMessage,
		hideCustomValueErrorMessage,
		formatAmountNumber,
		getCurrencyGroupSeparetor,
		getCurrencyDecimalSeparator,
		hoveredElement,
		setHoveredElement,
		isOtherValueHover,
		setIsOtherValueHover,
	};
};
/* cSpell:enable */
